'use client';
import 'react-day-picker/dist/style.css';
import '@/styles/day-picker.css';

import { autoPlacement, autoUpdate, offset, shift, useFloating } from '@floating-ui/react';
import { Slot } from '@radix-ui/react-slot';
import { isFuture } from 'date-fns';
import { ko } from 'date-fns/locale';
// import Image from '@/components/v2/Image';
import { useRef } from 'react';
import { DayPicker } from 'react-day-picker';
import { cx } from 'react-twc';
import { useBoolean, useOnClickOutside } from 'usehooks-ts';

import { useCalendar } from '@/hooks/useCalendar';

import Modal from '../Modal';
import Button from '../v4/Button';
import { formatCaption, MultiCalendarProps } from './MultiCalendar';
import RangeHint from './RangeHint';

const MultiCalendarMobile = ({
  range,
  setRange,
  className = '',
  children,
  showAllOption = true,
  maxRangeDays,
  minRangeDays,
  latestDate,
  storageKey,
  presentation = 'modal',
  gtmIds,
}: MultiCalendarProps) => {
  const lastSelectable = latestDate ?? new Date();
  const ref = useRef<HTMLDivElement>(null);

  const { value: isOpen, setTrue: showModal, setFalse: closeModal } = useBoolean(false);
  const isDropdown = presentation === 'dropdown';

  const { refs, floatingStyles } = useFloating({
    whileElementsMounted: autoUpdate,
    open: isOpen,
    middleware: [
      autoPlacement({
        autoAlignment: true,
        allowedPlacements: ['bottom', 'bottom-start', 'bottom-end'],
      }),
      offset(8),
      shift({ padding: 8 }),
    ],
  });

  const {
    currentOption,
    calendarRange,
    canConfirm,
    isPicking,
    setLast1Week,
    setLast2Week,
    setLast1Month,
    setAll,
    customSetRange,
    closeCalendar,
    confirmRange,
  } = useCalendar({
    minRangeDays,
    latestDate,
    range,
    setRange,
    closeModal,
    storageKey,
  });

  // 범위가 완성된 상태에서 min 을 주면 라이브러리가 범위 안쪽을 잠가 다시 고를 수 없다
  const limited = !minRangeDays || isPicking;

  // 모달은 ref 밖(포털)에 그려져 드롭다운에만 적용한다.
  // 닫힌 캘린더가 반응하면 같이 마운트된 다른 쪽의 프리셋 상태를 덮어쓴다
  useOnClickOutside(ref as any, () => isDropdown && isOpen && closeCalendar());

  const panel = (
    <div
      key="calendar"
      className={cx(
        'z-30 flex flex-col items-center gap-4 bg-white p-4',
        isDropdown ? 'rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.08)]' : 'rounded-lg',
      )}
    >
      <DayPicker
        locale={ko}
        mode="range"
        selected={calendarRange}
        onSelect={customSetRange}
        defaultMonth={lastSelectable}
        pagedNavigation
        captionLayout="dropdown-buttons"
        formatters={{
          formatCaption,
        }}
        fromYear={2023}
        toYear={new Date().getFullYear()}
        toMonth={lastSelectable}
        min={limited ? minRangeDays : undefined}
        max={limited ? maxRangeDays : undefined}
        disabled={(date) => isFuture(date) || date > lastSelectable}
      />

      <RangeHint range={calendarRange} minRangeDays={minRangeDays} maxRangeDays={maxRangeDays} />

      <div className="flex gap-2">
        <Button
          variant={currentOption === 'last1Week' ? 'tonal' : 'ghost'}
          color={currentOption === 'last1Week' ? 'orange' : 'coolgray'}
          data-gtm-id={gtmIds?.week}
          onClick={setLast1Week}
        >
          최근 1주
        </Button>
        <Button
          variant={currentOption === 'last2Week' ? 'tonal' : 'ghost'}
          color={currentOption === 'last2Week' ? 'orange' : 'coolgray'}
          data-gtm-id={gtmIds?.twoWeek}
          onClick={setLast2Week}
        >
          최근 2주
        </Button>
        <Button
          variant={currentOption === 'last1Month' ? 'tonal' : 'ghost'}
          color={currentOption === 'last1Month' ? 'orange' : 'coolgray'}
          data-gtm-id={gtmIds?.month}
          onClick={setLast1Month}
        >
          최근 1달
        </Button>
        {showAllOption && (
          <Button
            variant={currentOption === 'all' ? 'tonal' : 'ghost'}
            color={currentOption === 'all' ? 'orange' : 'coolgray'}
            onClick={setAll}
          >
            전체
          </Button>
        )}
      </div>
      <hr className="w-full" />
      <div className="flex gap-2 justify-end w-full">
        <Button variant="tonal" color="coolgray" className="w-20" onClick={closeCalendar}>
          취소
        </Button>
        <Button
          variant="contained"
          color="coolgray"
          className="w-20 disabled:opacity-40"
          onClick={confirmRange}
          disabled={!canConfirm}
          data-gtm-id={gtmIds?.submit}
        >
          확인
        </Button>
      </div>
    </div>
  );

  return (
    <div className={cx('relative w-fit', className)} ref={ref}>
      <div ref={refs.setReference}>
        <Slot onClick={showModal}>{children}</Slot>
      </div>

      {isDropdown ? (
        isOpen && (
          <div ref={refs.setFloating} style={floatingStyles} className="z-30">
            {panel}
          </div>
        )
      ) : (
        <Modal isOpen={isOpen} close={closeModal} direction="center" allowOutsideClickClose>
          {panel}
        </Modal>
      )}
    </div>
  );
};

export default MultiCalendarMobile;
